import React from 'react';
import Paper from '@material-ui/core/Paper';
import { withStyles } from '@material-ui/core/styles';
import { Cancel as CancelIcon, CheckCircle as CheckCircleIcon } from '@markinson/uicomponents-v2/SvgIcons';
import ICreditNotesearchProps, { ICreditNotesSearchHandle } from './CreditNotesSearch.properties';
import inlineStyles from './CreditNotesSearch.styles';
import { isNull, isShallowEqual } from 'utils/utils';
import BasicLookupActionField from 'components/FormView/Fields/BasicLookupActionField';
import { IBasicLookupFieldHandle } from '@markinson/uicomponents-v2/BasicLookupField/BasicLookup.properties';
import LoadingButton from 'components/common/LoadingButton';
import { useAsyncClickHandler } from 'utils/hooks';
import { DateTextField } from '@markinson/uicomponents-v2';
import { ENTER_KEY } from 'utils/constants';
import SelectContainer from 'components/SelectContainer';
import { ISelectFieldHandle } from '@markinson/uicomponents-v2/SelectField/SelectField.properties';
import { useCNContextSelector, useCNDispatch } from 'components/CreditNotes/CreditNotes.context';
import { useUpdateCreditNotesContext } from 'api/creditNotes/creditNotes';
import { cloneDeep } from 'lodash';
import { CreditNotesActionTypes } from 'components/CreditNotes/CreditNotes.actions';

const LOOKUP_FIELD_FOCUS_DELAY_TIME = 500;
const NON_LOOKUP_FIELD_FOCUS_DELAY_TIME = 200;
const UNDO_TIMEOUT = 100;

const CreditNotesearch = (props: ICreditNotesearchProps, ref: React.Ref<ICreditNotesSearchHandle>) => {
    const { classes, loading: loadingProp, formData, initialData, onApply } = props;
    const warehouseLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const customerLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const invoiceLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const creditReasonLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const entityLookupRef = React.useRef<IBasicLookupFieldHandle>(null);
    const periodFieldRef = React.useRef<ISelectFieldHandle>(null);
    const dateLookupRef = React.useRef(null);
    const [form, setFormValues] = React.useState<any>({});
    const [lookupCalculateRequested, setLookupCalculateRequested] = React.useState<boolean>(false);
    const FocusField = useCNContextSelector<'LHSFocusField'>((state) => state.LHSFocusField);
    const calculateCreditNotesContext = useUpdateCreditNotesContext();
    const CreditNoteId = useCNContextSelector<'CreditNote'>((state) => state.CreditNote);
    const creditNoteContext = useCNContextSelector<'CreditNotesContext'>((state) => state.CreditNotesContext);
    const contextDispatch = useCNDispatch();

    const loading = React.useMemo(() => loadingProp, [loadingProp]);

    const selectContainerApiPrefs = React.useMemo(
        () => ({
            LookupType: 'Dynamic',
            path: '/CustomTypes/Lookup/ActiveModulePeriod/Search',
            method: 'POST'
        }),
        []
    );

    const selectContainerParams = React.useMemo(
        () => ({
            SearchText: '',
            BatchPage: 1,
            BatchSize: 10,
            Module: 'iv'
        }),
        []
    );

    React.useEffect(
        () => {
            if (!loadingProp) {
                focusOnFieldBasedOnChangedField(FocusField);
            }
        },
        [loadingProp, entityLookupRef]
    );

    React.useEffect(
        () => {
            setFormValues({ ...formData });
        },
        [formData]
    );

    React.useImperativeHandle(
        ref,
        () => ({
            isDirty(): boolean {
                const { Note: __, ...transformedFormData } = formData;
                const { Note: _, ...transformedForm } = form;

                return !isShallowEqual(transformedFormData, transformedForm);
            },
            focusOnInvoiceField(): void {
                focusOnFieldBasedOnChangedField('Invoice');
            }
        })
    );

    const applyCallback = () => {
        if (validationPassed() && onApply) {
            onApply({ ...form });
        }
    };

    const handleUndoButtonClick = () => {
        setFormValues(initialData);
        contextDispatch({
            [CreditNotesActionTypes.setCreditNote]: CreditNoteId,
            [CreditNotesActionTypes.setCreditNotesContext]: initialData,
        });

    };

    const { onClick: onApplyClick } = useAsyncClickHandler(applyCallback, !loading && !lookupCalculateRequested);
    const { onClick: onUndoClick } = useAsyncClickHandler(handleUndoButtonClick, !loading && !lookupCalculateRequested);

    const updateFormField = (name, value) => {
        if (form[name] !== value) {
            setLookupCalculateRequested(true);
            setFormValues((prevForm) => ({
                ...prevForm,
                [name]: value
            }));

            if (value === '0' && ['Invoice'].includes(name)) {
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNotesContext]: { ...creditNoteContext, Invoice: null }
                });

                return;
            } else if (['CustomerId', 'CreditReason', 'Entity', 'Warehouse', 'Date'].includes(name) && (isNull(value) || value === '')) {
                contextDispatch({
                    [CreditNotesActionTypes.setCreditNotesContext]: { ...creditNoteContext, [name]: null }
                });

                return;
            }

            if (value === '' && ['Invoice'].includes(name)) {
                calculateCreditNotes(name, undefined);

                return;
            }

            calculateCreditNotes(name, value);
        }
    };
    const validationPassed = () => {
        const { CustomerId, CreditReason, Entity, Warehouse, Date }: any = form || {};

        return !isNull(Date) &&
            !isNull(Warehouse) && !isNull(Entity) && !isNull(CustomerId) && !isNull(CreditReason);
    };
    const focusOnFieldWithDelay = (fieldRef, FOCUS_DELAY_TIME: number) => {
        setTimeout(
            () => {
                fieldRef?.current?.focus();
            },
            FOCUS_DELAY_TIME
        );
    };

    const focusOnFieldBasedOnChangedField = (ChangedField: string) => {
        switch (ChangedField) {
            case 'Invoice':
                focusOnFieldWithDelay(invoiceLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'Customer':
                focusOnFieldWithDelay(customerLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'CustomerCreditReason':
                focusOnFieldWithDelay(creditReasonLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'Warehouse':
                focusOnFieldWithDelay(warehouseLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'Entity':
                focusOnFieldWithDelay(entityLookupRef, LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            case 'Date':
                focusOnFieldWithDelay(dateLookupRef, NON_LOOKUP_FIELD_FOCUS_DELAY_TIME);
                break;
            default:
        }
    };

    const calculateCreditNotes = async (ChangedField: string, value) => {
        setLookupCalculateRequested(false);

        const updatedCriteria = cloneDeep(form);
        const updatedValue = value;

        if (!updatedCriteria?.Freight) {
            updatedCriteria.Freight = 0;
        }
        if (!updatedCriteria?.InvoiceTotal) {
            updatedCriteria.InvoiceTotal = 0;
        }

        const response = await calculateCreditNotesContext.mutateAsync({
            WorkCreditNoteId: CreditNoteId || 0,
            ...updatedCriteria,
            [ChangedField]: updatedValue,
            urlQueryParams: {
                ChangedField
            }
        });
        //   const response = { Status: true, CreditNotesContext: { inlineObject: { ...updatedCriteria } } };

        if (response && response?.Status) {
            contextDispatch({
                [CreditNotesActionTypes.setCreditNotesContext]: response.CreditNotesContext?.inlineObject,
            });
            setFormValues({
                ...response?.CreditNotesContext?.inlineObject,
            });
        } else if (!response) {
            // rest the form values the previous one
            if (creditNoteContext) {
                setFormValues(creditNoteContext);
            }

        }
    };

    const isLookupInvalid = (value, formField: string) => {
        if (isNull(value) || value === '') {
            updateFormField(formField, value);
            setLookupCalculateRequested(false);
        }
    };

    return (
        <React.Fragment>
            <Paper style={inlineStyles.lookupFieldsPaperContainer} square={true}>
                <BasicLookupActionField
                    className={classes.basicLookupField}
                    label={'Invoice'}
                    size={'large'}
                    searchScreenTitle={'Invoice Lookup'}
                    params={{ Entity: form?.Entity || undefined, CustomerId: form?.CustomerId && Number(form?.CustomerId) || undefined, CompletedInvoice: true }}
                    disableOptionsLookup={true}
                    lookupName={'SalesInvoiceSearch'}
                    value={form?.Invoice?.toString() === '0' ? undefined : form?.Invoice?.toString()}
                    onSelectedItemChange={(v: any, searchText) => { updateFormField('Invoice', v?.Details?.InvoiceNumber || searchText); }}
                    suppressDescription={true}
                    suppressValidation={true}
                    disabled={loading || CreditNoteId > 0}
                    ref={invoiceLookupRef}
                    descriptionField={'Label'}
                    onEnterKeyDown={onApplyClick}
                    searchScreenAPIParms={{ Type: 'LOOKUP' }}
                    required={false} // on integration, need to add some variable to determine if this is required
                    shouldEnterBehaveAsTab={true}
                    onInvalidChange={(invalid) => {
                        if (invalid) {
                            focusOnFieldBasedOnChangedField('Invoice');
                        }
                    }}
                />
                <BasicLookupActionField
                    className={classes.basicLookupField}
                    label={'Customer'}
                    size={'large'}
                    searchScreenTitle={'Customer Lookup'}
                    lookupName={'Customer'}
                    value={form?.CustomerId}
                    onSelectedItemChange={(v) => { if (v?.Code) updateFormField('CustomerId', v.Code); }}
                    suppressDescription={true}
                    disabled={loading || CreditNoteId > 0}
                    ref={customerLookupRef}
                    descriptionField={'Label'}
                    onEnterKeyDown={onApplyClick}
                    searchScreenAPIParms={{ Type: 'LOOKUP' }}
                    required={true}
                    onInvalidChange={(invalid) => {
                        if (invalid) {
                            focusOnFieldBasedOnChangedField('Customer');
                        }
                    }}
                    onBlur={(value) => { isLookupInvalid(value, 'CustomerId'); }}
                />
                <BasicLookupActionField
                    label={'Credit reason'}
                    className={classes.mediumSizeLookupField}
                    size={'small'}
                    lookupName={'CustomerCreditReason'}
                    value={form?.CreditReason}
                    onSelectedItemChange={(v) => { if (v?.Code) updateFormField('CreditReason', v.Code); }}
                    suppressDescription={true}
                    required={true}
                    disabled={loading}
                    ref={creditReasonLookupRef}
                    onEnterKeyDown={onApplyClick}
                    shouldEnterBehaveAsTab={true}
                    onInvalidChange={(invalid) => {
                        if (invalid) {
                            focusOnFieldBasedOnChangedField('CustomerCreditReason');
                        }
                    }}
                     onBlur={(value) => { isLookupInvalid(value, 'CreditReason'); }}
                />
            </Paper>
            <Paper style={inlineStyles.lookupFieldsPaperContainer} square={true}>
                <BasicLookupActionField
                    className={classes.mediumSizeLookupField}
                    label={'Entity'}
                    size={'small'}
                    lookupName={'Entity'}
                    value={form?.Entity}
                    onSelectedItemChange={(v) => { if (v?.Code) updateFormField('Entity', v.Code); }}
                    suppressDescription={true}
                    required={true}
                    disabled={loading || CreditNoteId > 0}
                    ref={entityLookupRef}
                    onEnterKeyDown={onApplyClick}
                    shouldEnterBehaveAsTab={true}
                    onInvalidChange={(invalid) => {
                        if (invalid) {
                            focusOnFieldBasedOnChangedField('Entity');
                        }
                    }}
                    onBlur={(value) => { isLookupInvalid(value, 'Entity'); }}
                />
                <BasicLookupActionField
                    className={classes.mediumSizeLookupField}
                    label={'Warehouse'}
                    size={'small'}
                    searchScreenTitle={'Warehouse Lookup'}
                    lookupName={'WarehouseEntity'}
                    value={form?.Warehouse?.toString() ?? ''}
                    onSelectedItemChange={(v) => { if (v?.Code) updateFormField('Warehouse', v.Code); }}
                    suppressDescription={true}
                    disabled={loading || CreditNoteId > 0}
                    ref={warehouseLookupRef}
                    descriptionField={'Label'}
                    onEnterKeyDown={onApplyClick}
                    searchScreenAPIParms={{ Type: 'LOOKUP' }}
                    required={true}
                    onInvalidChange={(invalid) => {
                        if (invalid) {
                            focusOnFieldBasedOnChangedField('Warehouse');
                        }
                    }}
                    onBlur={(value) => { isLookupInvalid(value, 'Warehouse'); }}
                />
                <div className={classes.selectContainer}>
                    <SelectContainer
                        label={'Period'}
                        innerRef={periodFieldRef}
                        className={classes.field}
                        disabled={loading || CreditNoteId > 0}
                        value={(form?.Period || '').toString()}
                        apiPrefs={selectContainerApiPrefs}
                        params={selectContainerParams}
                        onChange={(v) => {
                            updateFormField('Period', v);
                        }}
                    />
                </div>
                <DateTextField
                    label={'Date'}
                    value={form?.Date}
                    style={inlineStyles.dateField}
                    disabled={loading || CreditNoteId > 0}
                    defaultValue={false}
                    onChange={(v) => { updateFormField('Date', v); }}
                    required={true}
                    fieldRef={dateLookupRef}
                    onKeyDown={({ keyCode }) => {
                        if (keyCode === ENTER_KEY) {
                            if (dateLookupRef.current) {
                                dateLookupRef.current.blur();
                            }
                            onApplyClick();
                        }
                    }}
                />
            </Paper>
            <Paper style={inlineStyles.commitActionPaperContainer} square={true}>
                <LoadingButton
                    onClick={() => {
                        setTimeout(
                            () => {
                                onUndoClick();
                            },
                            UNDO_TIMEOUT
                        );
                    }}
                    variant={'contained'}
                    disabled={loading}
                    className={classes.commitActionButton}
                    loading={loading}
                    startIcon={<CancelIcon style={{ color: 'red' }} />}>
                    UNDO
                </LoadingButton>
                <LoadingButton
                    onClick={() => {
                        setTimeout(
                            () => {
                                onApplyClick();
                            },
                            0
                        );
                    }}
                    variant={'contained'}
                    disabled={loading || !validationPassed()}
                    className={classes.commitActionButton}
                    loading={loading}
                    startIcon={<CheckCircleIcon style={{ color: 'green' }} />}>
                    APPLY
                </LoadingButton>
            </Paper>
        </React.Fragment>
    );
};

export default withStyles(inlineStyles, { index: 1 })(React.memo(React.forwardRef(CreditNotesearch)));
